import { useReducer, createContext, Dispatch } from 'react';

import { CRUDableType } from '@/meta';
import { assertUnreachable } from '@/utils';

export type Message = {
  title: string;
  message: string;
  fieldErrors?: { field: string; error: string }[];
  skipAnalysis?: boolean;
};

export type MessageError = Message & Partial<Error>;

const initialCache: Record<CRUDableType, number> = {
  slo: 0,
  capability: 0,
  team: 0,
  product: 0,
  slogroup: 0,
};

const initialMessages: Messages = {
  status: {
    title: '',
    message: '',
  },
  error: {
    name: '',
    title: '',
    message: '',
    stack: '',
  },
};

type Messages = {
  status: Message;
  error: MessageError;
};

type RootState = {
  cache: Record<CRUDableType, number>;
  messages: Messages;
};

type Action =
  | {
      type: 'cache';
      data: Partial<RootState['cache']>;
    }
  | {
      type: 'messages.status';
      data: Messages['status'];
    }
  | {
      type: 'messages.error';
      data: Messages['error'];
    };

const defaultState: RootState = {
  cache: initialCache,
  messages: initialMessages,
};

function reducer(state: RootState, action: Action): RootState {
  switch (action.type) {
    case 'cache':
      return {
        ...state,
        cache: {
          ...state.cache,
          ...action.data,
        },
      };
    case 'messages.error':
      return {
        ...state,
        messages: {
          ...state.messages,
          error: action.data,
        },
      };
    case 'messages.status':
      return {
        ...state,
        messages: {
          ...state.messages,
          status: action.data,
        },
      };
    default:
      return assertUnreachable(action);
  }
}

export const RootStateContext = createContext(defaultState);
export const RootStateDispatchContext = createContext<Dispatch<Action>>(() => {});

export default function() {
  return useReducer(reducer, defaultState);
}
